import "./App.css";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatHome } from "./CometChat/components/CometChatHome/CometChatHome";
import CometChatLogin from "./CometChat/components/CometChatLogin/CometChatLogin";
import { AppContextProvider } from "./CometChat/context/AppContext";
import {
  CometChatProvider,
  useCometChatContext,
} from "./CometChat/context/CometChatContext";
import "@cometchat/chat-uikit-react/css-variables.css";
import { dlog } from "./CometChat/utils/debugLog";
import { DebugErrorBoundary } from "./CometChat/components/DebugErrorBoundary";
import useSystemColorScheme from "./CometChat/customHooks";
import useThemeStyles from "./CometChat/customHook/useThemeStyles";

/**
 * Reads the brand color, typography, and font-size settings from
 * CometChatSettings (via the CometChatProvider context) and writes them
 * to the corresponding CSS variables on the chat root element.
 *
 * This was previously done by CometChatApp.tsx, which we no longer
 * wrap CometChatHome with — our App.tsx renders CometChatHome directly
 * because the auto-login Shopify flow needed a different structure.
 * Without this component the chat would fall back to the UI Kit's
 * default purple `#6852d6` instead of the brand color you've set.
 *
 * Why we also need the `chatReady` prop:
 * `useThemeStyles` looks up an element with id `${theme}-theme` and
 * silently returns if it's missing. That element is rendered by
 * CometChatHome — which doesn't mount until our 1500ms stabilization
 * timer elapses. The hook's first runs (during `isAuthChecking` and the
 * "Connecting to chat..." phase) all bail before painting anything.
 * Once chatReady flips to true and CometChatHome paints its root
 * element, we force the hook to re-run by pushing a fresh styleFeatures
 * object reference into context — that change in identity satisfies
 * the hook's dependency array and it picks the brand color back up.
 *
 * Renders nothing — it's purely an effect carrier.
 */
function ThemeApplier({
  loggedInUser,
  chatReady,
}: {
  loggedInUser: CometChat.User | null;
  chatReady: boolean;
}) {
  const { styleFeatures, setStyleFeatures } = useCometChatContext();
  const systemTheme = useSystemColorScheme();
  useThemeStyles(styleFeatures, systemTheme, setStyleFeatures, loggedInUser);

  useEffect(() => {
    if (chatReady) {
      // Pushing a shallow-copied object forces useThemeStyles' useEffect
      // to re-fire (its dependency array compares by reference). The
      // values themselves don't change.
      setStyleFeatures((prev) => ({ ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatReady]);

  return null;
}

/**
 * Root app shell.
 *
 * This component owns one piece of state: whether we have a logged-in
 * CometChat user. It listens for three signals to decide:
 *
 *   1. The CometChat SDK's LoginListener (loginSuccess / logoutSuccess)
 *      — fires when the SDK actively logs in or out *during this page
 *      load*.
 *
 *   2. A custom `cometchat-session-detected` window event — fired by
 *      CometChatLogin's polling path when it discovers the SDK already
 *      has a session that was established by a previous page load or
 *      a sibling iframe. In that case the SDK doesn't fire a fresh
 *      loginSuccess for *us*, so without this signal we'd stay stuck on
 *      the login screen forever even though the user is authenticated.
 *
 *   3. `pageshow` with `persisted=true` — bfcache restore. The SDK's
 *      in-memory state can be stale in this path, so we re-fetch the
 *      logged-in user.
 *
 * What this component intentionally does NOT do:
 *   - Wipe localStorage on visibilitychange / focus / pageshow. The
 *     previous version did, which caused a perpetual "Connecting to
 *     secure chat..." loop in webviews where those events fire
 *     constantly.
 */

const LOCK_KEY = "cometchat_login_in_progress";
// Locks older than this from a previous page load are obviously stale —
// real logins finish in well under 5s. We clear those at boot so a
// crashed prior load can't keep new loads polling forever.
const STALE_LOCK_MAX_AGE_MS = 10000;

/**
 * Reconcile the SDK user's display name AND metadata with the
 * corresponding URL parameters.
 *
 * Why this exists in App.tsx (and not just in CometChatLogin):
 * CometChatLogin only mounts when the user isn't already authenticated.
 * On returning visits, the cached SDK session resolves immediately,
 * App.tsx jumps straight to CometChatHome, and CometChatLogin's
 * updateUserName logic never runs — so changes in Shopify (whether to
 * the customer's name, address, member number, or country-club list)
 * would never propagate to CometChat. This function re-checks on every
 * page load, regardless of how authentication happened, and updates
 * only when the values actually differ from what's stored.
 *
 * Both the name and metadata are read from URL params:
 *   - `?name=...`     a string (URL-encoded)
 *   - `?metadata=...` a JSON object (URL-encoded)
 *
 * The metadata is whatever shape Shopify Liquid built. Currently:
 *   { memberNumber, location: {city,state,country}, countryClubs: [{name,state}] }
 * but we store it as opaque JSON — display-side code in
 * CometChatUserDetails decides which fields it knows how to render.
 */
function refreshUserDetailsFromUrl(user: CometChat.User): void {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }

  const rawName = (params.get("name") ?? "").trim();
  const rawMetadata = params.get("metadata");

  let parsedMetadata: Record<string, unknown> | null = null;
  if (rawMetadata) {
    try {
      const decoded = JSON.parse(rawMetadata);
      if (decoded && typeof decoded === "object" && !Array.isArray(decoded)) {
        parsedMetadata = decoded as Record<string, unknown>;
      }
    } catch (err) {
      dlog.warn("Could not parse metadata URL param:", err);
    }
  }

  // Compare to current values to decide if an update is actually
  // needed. JSON.stringify is fine for shallow-ish metadata; if the
  // schema grows complex we can switch to a deep-equal helper.
  const nameChanged = !!rawName && rawName !== user.getName();
  const currentMetadata = (() => {
    try {
      return user.getMetadata?.() ?? null;
    } catch {
      return null;
    }
  })();
  const metadataChanged =
    parsedMetadata !== null &&
    JSON.stringify(parsedMetadata) !== JSON.stringify(currentMetadata);

  if (!nameChanged && !metadataChanged) {
    dlog.log(
      `User details already up to date (name="${user.getName()}", metadata unchanged).`,
    );
    return;
  }

  const updated = new CometChat.User(user.getUid());
  // CometChat's updateCurrentUserDetails ignores fields we don't set,
  // so we can selectively update only what changed. We always set name
  // either way (otherwise CometChat may overwrite it server-side with
  // an empty string in some SDK versions) — this is a safe no-op when
  // the name hasn't changed.
  updated.setName(rawName || user.getName());
  if (metadataChanged && parsedMetadata) {
    updated.setMetadata(parsedMetadata);
  }
  CometChat.updateCurrentUserDetails(updated)
    .then(() =>
      dlog.info(
        `User details refreshed from URL. nameChanged=${nameChanged} metadataChanged=${metadataChanged}`,
      ),
    )
    .catch((err) =>
      dlog.warn("Could not refresh user details from URL:", err),
    );
}

function clearStaleLockIfAny(): void {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const age = Date.now() - (parsed?.timestamp ?? 0);
    if (age > STALE_LOCK_MAX_AGE_MS) {
      dlog.warn(`Clearing stale boot-time lock (age=${age}ms).`);
      localStorage.removeItem(LOCK_KEY);
    }
  } catch {
    // localStorage unavailable / corrupt value — best effort, just nuke it.
    try {
      localStorage.removeItem(LOCK_KEY);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Wipe every CometChat-related key from localStorage.
 *
 * Used when the server tells us the cached auth token is invalid (e.g.,
 * the user was deleted from the dashboard).
 *
 * Why match this aggressively: the CometChat JS SDK doesn't always
 * prefix its keys with the literal string "cometchat" — it commonly
 * uses the app ID as a prefix (e.g. `16746429daf75039a__user`),
 * `chatsdk_*`, `ChatSDK_*`, etc. Earlier versions of this function only
 * matched keys containing the word "cometchat" and silently missed
 * everything else. This widened matcher catches:
 *   - any key containing "cometchat" (case-insensitive)
 *   - any key containing "chatsdk" (case-insensitive)
 *   - any key starting with the configured CometChat app ID
 *   - our own login lock key
 *
 * In practice the upstream `CometChat.logout()` call also takes care of
 * clearing these — this is a fallback for keys that slipped past it.
 */
function wipeCometChatStorage(): void {
  try {
    const appId = (process.env.REACT_APP_COMETCHAT_APP_ID || "").toLowerCase();
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      const lower = k.toLowerCase();
      const matches =
        lower.includes("cometchat") ||
        lower.includes("chatsdk") ||
        (appId && lower.startsWith(appId)) ||
        k === LOCK_KEY;
      if (matches) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    dlog.warn(
      `Wiped ${keysToRemove.length} stale CometChat localStorage keys.`,
    );
  } catch {
    /* ignore — partitioned storage may throw */
  }
}

// How long we wait after `loginSuccess` before mounting CometChatHome.
// This pads past the SDK's transient auth-flap window observed in
// WKWebView: after a real login, the SDK can flip-flop loginSuccess /
// logoutSuccess for over a second before settling, and during those
// flaps any request requiring an authToken (getConversations,
// getGroups) fails with `USER_NOT_LOGED_IN`. Sized to cover the flap
// window in our worst observed run; cheap to bump higher if needed.
const SDK_STABILIZATION_DELAY_MS = 1500;

function App() {
  const [loggedInUser, setLoggedInUser] = useState<CometChat.User | null>(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [loginKey, setLoginKey] = useState(0);
  // Becomes true once the SDK has been quiet — no logoutSuccess events —
  // for SDK_STABILIZATION_DELAY_MS milliseconds following a login. This
  // gates the actual mount of CometChatHome so child components don't
  // fire network requests during the spurious-logout window.
  const [chatReady, setChatReady] = useState(false);
  const lastCheckRef = useRef<number>(0);

  const recheckSession = useCallback((reason: string) => {
    // Debounce rapid-fire rechecks (webview events can fire in bursts).
    const now = Date.now();
    if (now - lastCheckRef.current < 1000) {
      dlog.log(`Skipping session recheck (debounced). reason=${reason}`);
      return;
    }
    lastCheckRef.current = now;

    dlog.log(`Re-checking session. reason=${reason}`);
    CometChat.getLoggedinUser().then(
      (user) => {
        if (user) {
          dlog.info(`Session recheck: logged in as ${user.getUid()}`);
          setLoggedInUser(user);
          refreshUserDetailsFromUrl(user);
        } else {
          dlog.log("Session recheck: no user; staying on login screen.");
          setLoggedInUser(null);
          setLoginKey((k) => k + 1);
        }
        setIsAuthChecking(false);
      },
      (err) => {
        dlog.error("Session recheck failed:", err);
        setLoggedInUser(null);
        setLoginKey((k) => k + 1);
        setIsAuthChecking(false);
      },
    );
  }, []);

  // Stabilization timer.
  //
  // Once a user is set we wait SDK_STABILIZATION_DELAY_MS before flipping
  // chatReady to true and mounting CometChatHome. After chatReady is
  // true we deliberately never set it back to false from this hook — the
  // SDK flap-fires logoutSuccess events for several seconds and we don't
  // want to unmount/remount the chat tree each time. Real logouts are
  // handled by clearing loggedInUser, which causes this effect to run
  // again with a null user and reset chatReady.
  useEffect(() => {
    if (!loggedInUser) {
      setChatReady(false);
      return;
    }
    if (chatReady) {
      // Already stable — don't reset on every loginSuccess re-fire.
      return;
    }
    dlog.log(
      `Starting SDK stabilization timer (${SDK_STABILIZATION_DELAY_MS}ms).`,
    );
    const timer = window.setTimeout(() => {
      dlog.info("SDK stabilization complete — mounting CometChatHome.");
      setChatReady(true);
    }, SDK_STABILIZATION_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [loggedInUser, chatReady]);

  useEffect(() => {
    const listenerID = "redan_global_listener";

    // Boot-time hygiene: kill an obviously-stale lock so CometChatLogin
    // doesn't immediately drop into the polling path on a fresh load.
    clearStaleLockIfAny();

    // Initial load — check whether the SDK already has a session in storage.
    CometChat.getLoggedinUser().then(
      (user) => {
        dlog.info(
          `Initial getLoggedinUser resolved. user=${user?.getUid() ?? "null"}`,
        );
        setLoggedInUser(user ?? null);
        setIsAuthChecking(false);
        // For returning visits the user is cached and CometChatLogin
        // never runs, which means its updateUserName step gets skipped.
        // Reconcile the display name with the URL ourselves.
        if (user) refreshUserDetailsFromUrl(user);
      },
      (err) => {
        dlog.error("Initial getLoggedinUser failed:", err);
        setIsAuthChecking(false);
      },
    );

    CometChat.addLoginListener(
      listenerID,
      new CometChat.LoginListener({
        loginSuccess: (user: CometChat.User) => {
          dlog.info(`LoginListener.loginSuccess uid=${user.getUid()}`);
          setLoggedInUser(user);
          // CometChatLogin's `updateUserName` already covers the
          // fresh-login case before this fires, so this call is
          // usually a no-op (URL name === current name). Kept here as
          // a safety net for any path that fires loginSuccess without
          // having gone through CometChatLogin first.
          refreshUserDetailsFromUrl(user);
        },
        logoutSuccess: () => {
          // Deliberately ignored.
          //
          // This app auto-logs-in based on URL params and offers no
          // user-facing logout button. Every logoutSuccess we see in
          // the wild is therefore spurious — produced by the SDK
          // flap-firing during its WKWebView auth dance, by a duplicate
          // login race in a sibling iframe, etc. Reacting to them was
          // making things drastically worse: we'd null out
          // loggedInUser, which made App.tsx render CometChatLogin,
          // which fired another login(), which collided with the SDK's
          // in-flight login and threw LOGIN_IN_PROGRESS, which fed back
          // into more flap events.
          //
          // If the session legitimately expires server-side, future SDK
          // calls will surface auth errors and we can handle those at
          // the call site rather than chasing a global event whose
          // signal-to-noise ratio is terrible.
          dlog.log("LoginListener.logoutSuccess (ignored).");
        },
      }),
    );

    // pageshow with persisted=true is the bfcache restore signal; this
    // is the one case where the in-memory React state may be out of sync
    // with the SDK's stored session.
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        recheckSession("pageshow.persisted");
      }
    };
    window.addEventListener("pageshow", handlePageShow);

    // Custom event: CometChatLogin's polling code dispatches this when
    // it discovers an authenticated session that was established by a
    // sibling/previous page load. The SDK won't fire a loginSuccess for
    // us in that path, so this is the only signal we get.
    const handleSessionDetected = () => {
      dlog.info("Received cometchat-session-detected event.");
      recheckSession("session-detected");
    };
    window.addEventListener(
      "cometchat-session-detected",
      handleSessionDetected,
    );

    /**
     * Recover from a 401 by reloading the page.
     *
     * Why a reload instead of in-place state surgery: we tried both
     * leaving the SDK alone (which left a stale in-memory user cache
     * causing wasted "Already authenticated" attempts) and calling
     * `CometChat.logout()` (which also clears the SDK's *init* state,
     * causing the next login() to fail with
     * COMETCHAT_INITIALIZATION_NOT_DONE). The SDK's lifecycle isn't
     * designed to be reset mid-session.
     *
     * Reloading the page makes the SDK re-initialize cleanly from
     * scratch in `index.tsx`, the auth-cleared localStorage means
     * `getLoggedinUser` returns null, and CometChatLogin runs the
     * full register-then-login flow on the fresh boot. End-to-end
     * recovery is faster and more deterministic than trying to nudge
     * the in-flight SDK back into a sane state.
     *
     * The sessionStorage cooldown prevents an infinite reload loop in
     * the unlikely case that the new user gets deleted between
     * registration and the first API call, or any other scenario
     * where the recovery itself produces another 401.
     */
    const RECOVERY_COOLDOWN_KEY = "cc_last_auth_recovery";
    const RECOVERY_COOLDOWN_MS = 30000;

    const handleAuthInvalid = (e: Event) => {
      const detail =
        (e as CustomEvent<{ reason?: string }>).detail?.reason ?? "unknown";

      let last = 0;
      try {
        last = parseInt(
          sessionStorage.getItem(RECOVERY_COOLDOWN_KEY) ?? "0",
          10,
        );
      } catch {
        /* sessionStorage may throw in partitioned iframe contexts */
      }
      const now = Date.now();

      if (now - last < RECOVERY_COOLDOWN_MS) {
        // We recently reloaded for recovery and got 401 again. Don't
        // loop — leave the user on the login screen so they can
        // contact support if the new account also can't authenticate.
        dlog.error(
          `Auth-invalid event ${now - last}ms after last recovery. Aborting reload to avoid loop.`,
        );
        setLoggedInUser(null);
        setChatReady(false);
        setLoginKey((k) => k + 1);
        return;
      }

      try {
        sessionStorage.setItem(RECOVERY_COOLDOWN_KEY, String(now));
      } catch {
        /* sessionStorage write may fail; reload still works */
      }

      dlog.warn(
        `Auth-invalid recovery: wiping storage and reloading. reason=${detail}`,
      );
      wipeCometChatStorage();
      // Use a microtask delay so the dlog line above has a chance to
      // flush to the on-screen panel before we navigate.
      setTimeout(() => window.location.reload(), 50);
    };
    window.addEventListener("cometchat-auth-invalid", handleAuthInvalid);

    return () => {
      CometChat.removeLoginListener(listenerID);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener(
        "cometchat-session-detected",
        handleSessionDetected,
      );
      window.removeEventListener("cometchat-auth-invalid", handleAuthInvalid);
    };
  }, [recheckSession]);

  return (
    <div className="App">
      <CometChatProvider>
        <AppContextProvider>
          {/* AppContextProvider's typed `children: ReactElement` only
              accepts a single child, so we wrap the ThemeApplier and
              the conditional render in a Fragment. */}
          <>
            <ThemeApplier
              loggedInUser={loggedInUser}
              chatReady={chatReady}
            />
            {isAuthChecking ? (
              <div className="loading-screen">Verifying Session...</div>
            ) : loggedInUser && chatReady ? (
              <DebugErrorBoundary label="CometChatHome">
                {/*
                  We pass loggedInUser down explicitly because of a race
                  we hit in WKWebView (Kin mobile app): the SDK's
                  loginSuccess event fires with a valid user, but for
                  ~400ms afterward CometChat.getLoggedinUser() and
                  CometChatUIKitLoginListener.getLoggedInUser() both
                  return null. App.tsx has the user from the event
                  callback; passing it down avoids the race.
                */}
                <CometChatHome injectedLoggedInUser={loggedInUser} />
              </DebugErrorBoundary>
            ) : loggedInUser ? (
              // Logged in but waiting for the SDK to stabilize. Show
              // the same loading screen the user already saw during
              // login so the transition isn't jarring.
              <div className="loading-screen">Connecting to chat...</div>
            ) : (
              <CometChatLogin key={loginKey} />
            )}
          </>
        </AppContextProvider>
      </CometChatProvider>
    </div>
  );
}

export default App;
